import { SxProps, Theme } from "@mui/material"

// Per-consultant colours for the weighted split. The split bar renders as
// "underline" chips: a white chip with a coloured bottom rule and coloured
// text, so each hue carries the roles that treatment needs:
//   line: the 3px underline accent
//   text: a darkened variant, legible as ink on a white chip
//   fill, solid version, for the small legend swatch
//   tint: a faint wash, kept for optional tinted treatments
// Muted, earthy hues chosen so the bar reads calm (per Mayer's "too bold");
// identity is carried by the row label + legend, not colour alone, so this set
// doesn't need to clear the strict categorical-CVD bar. Cycles past 8.
export interface SplitHue {
  fill: string
  line: string
  text: string
  tint: string
}

export const SPLIT_HUES: SplitHue[] = [
  { fill: "#4f7fc4", line: "#4f7fc4", text: "#35598f", tint: "#edf2fa" }, // blue
  { fill: "#3f9b79", line: "#3f9b79", text: "#2c7256", tint: "#ebf5f0" }, // sage
  { fill: "#cf9a4a", line: "#b67e22", text: "#8a5e17", tint: "#f9f1e2" }, // amber
  { fill: "#836fa6", line: "#836fa6", text: "#5b4a7d", tint: "#f0edf7" }, // plum
  { fill: "#cf7b9a", line: "#c56d8e", text: "#a04d69", tint: "#faeef2" }, // rose
  { fill: "#607581", line: "#607581", text: "#44555f", tint: "#eef2f4" }, // slate
  { fill: "#3f8f92", line: "#3f8f92", text: "#2b6a6d", tint: "#e9f3f4" }, // teal
  { fill: "#c47a5c", line: "#b56c4e", text: "#8f5237", tint: "#f7efe9" }, // clay
]

export const splitHue = (index: number): SplitHue => {
  const n = SPLIT_HUES.length
  return SPLIT_HUES[((index % n) + n) % n]
}

// Soft tinted chip styling, calmer than a solid filled MUI chip, using the
// design system's alert tints (dark text on a faint wash). Reserve `warn` / `bad`
// for genuine problems so strong colour stays meaningful; `neutral` is the
// at-rest state.
type Tint = "neutral" | "good" | "warn" | "bad"

export const chipTintSx = (tint: Tint): SxProps<Theme> => {
  const tints: Record<Tint, { bg: string; fg: string }> = {
    neutral: { bg: "action.hover", fg: "text.secondary" },
    good: { bg: "success.alert.background", fg: "success.alert.content" },
    warn: { bg: "warning.alert.background", fg: "warning.alert.content" },
    bad: { bg: "error.alert.background", fg: "error.alert.content" },
  }
  return {
    bgcolor: tints[tint].bg,
    color: tints[tint].fg,
    fontWeight: 400,
    border: "none",
  }
}
